/**
 * Выбранные программы заказа обучения (training-program-picker; ТЗ 26.10.2026, п.4.5): по каждой
 * программе — строка программы (п.4.3) и «X слушателей», ниже «Сотрудник — Должность», первые
 * пять, остальные — «Развернуть ещё». Тот же блок — на шаге выбора и на подтверждении.
 *
 * Пакет (training-order-packages; ответ заказчицы 27.10, В9) — одной группой «Пакет «…»»: его
 * программы строками программ, одно количество и один список слушателей; убирается целиком.
 *
 * Число слушателей (training-listeners-count; ТЗ п.1.3) — заявленное число мест: сотрудников можно
 * назвать не всех — тогда под заголовком «Указано N из M».
 */
import { useState, type ReactNode } from 'react'
import type {
  TrainingPackageComposition,
  TrainingProgramComposition,
  TrainingSelectedEmployee,
} from '@/lib/api'
import { Badge } from '@/design-system/components/primitives/badge'
import { Button } from '@/design-system/components/primitives/button'
import { pluralRu } from '@/lib/orders'
import { cn } from '@/lib/utils'
import { formatRubles } from '@/lib/training-catalog'
import { ProgramTitle } from './program-title'

/**
 * Сколько слушателей видно сразу: ни одного — список сотрудников по умолчанию свёрнут (ответ
 * заказчицы 04.11 к п.4.5; в ТЗ 4.5 было «первые пять»).
 */
export const LISTENERS_PREVIEW = 0

/** «Показать 3 сотрудников» / «Свернуть» — кнопка списка слушателей позиции. */
export function listenersToggleLabel(expanded: boolean, count: number): string {
  if (expanded) return 'Свернуть сотрудников'
  return `Показать ${count} ${pluralRu(count, 'сотрудника', 'сотрудников', 'сотрудников')}`
}

/** «Указано 3 из 5» — сколько слушателей из заявленных уже названо (training-listeners-count). */
export function namedOfPlaces(named: number, places: number): string {
  return `Указано ${named} из ${places}`
}

function plural(n: number, one: string, few: string, many: string): string {
  const mod10 = n % 10
  const mod100 = n % 100
  if (mod10 === 1 && mod100 !== 11) return one
  if (mod10 >= 2 && mod10 <= 4 && (mod100 < 12 || mod100 > 14)) return few
  return many
}

/** «1 слушатель», «3 слушателя», «7 слушателей». */
export function listenersCount(n: number): string {
  return `${n} ${plural(n, 'слушатель', 'слушателя', 'слушателей')}`
}

/** Пометка у программы, которой больше нет в каталоге (черновик). */
export const ARCHIVED_PROGRAM_NOTE =
  'Программы больше нет в каталоге — уберите её из заказа и выберите другую.'
/** Пометка у пакета, который выбрать уже нельзя (черновик). */
export const UNAVAILABLE_PACKAGE_NOTE =
  'Пакета больше нет в каталоге — уберите его из заказа и выберите программы отдельно.'
/**
 * Те же пометки у возвращённого на доработку заказа: его состав команда уже приняла, убирать
 * программу или пакет не обязательно — только новых слушателей не добавить.
 */
export const ARCHIVED_PROGRAM_NOTE_RETURNED =
  'Программы больше нет в каталоге — новых слушателей не добавить.'
/**
 * Заказ «На заполнении»: состав меняют только через согласование — убрать позицию можно, предложив
 * изменения кнопкой «Изменить состав» (training-review-3).
 */
export const ARCHIVED_PROGRAM_NOTE_FILLING =
  'Программы больше нет в каталоге — уберите её через «Изменить состав», изменения согласует Центрика.'
export const UNAVAILABLE_PACKAGE_NOTE_FILLING =
  'Пакета больше нет в каталоге — уберите его через «Изменить состав», изменения согласует Центрика.'
export const UNAVAILABLE_PACKAGE_NOTE_RETURNED =
  'Пакета больше нет в каталоге — слушателей можно только убрать, новых не добавить.'

/**
 * Позиция выбранного: заголовок (строка программы или пакета), «X слушателей» и действия, пометка,
 * ниже «Сотрудник — Должность» — первые пять, остальные по «Развернуть ещё».
 */
function SelectedListeners({
  title,
  label,
  places,
  personIds,
  employees,
  countControl,
  actions,
  note,
}: {
  title: ReactNode
  /** Название для списка слушателей (доступное имя). */
  label: string
  /** Число мест (заявленное, не меньше названных). */
  places: number
  personIds: string[]
  employees: Map<string, TrainingSelectedEmployee>
  /** Поле числа слушателей вместо бейджа — на шаге выбора (training-listeners-count). */
  countControl?: ReactNode
  actions?: ReactNode
  note?: string
}) {
  const [expanded, setExpanded] = useState(false)
  const people = personIds
    .map((id) => employees.get(id))
    .filter((e): e is TrainingSelectedEmployee => Boolean(e))
  // Число мест — заявленное (сервер, не меньше названных); в списке — только сотрудники с данными
  // (устаревший id без пары в выбранных не печатается).
  const named = personIds.length
  const shown = expanded ? people : people.slice(0, LISTENERS_PREVIEW)
  const rest = people.length - LISTENERS_PREVIEW
  return (
    <li className="flex flex-col gap-2 p-3 nw:px-4">
      <div className="flex flex-wrap items-start gap-x-3 gap-y-2">
        <div className="min-w-0 flex-1 basis-64">{title}</div>
        <div className="ml-auto flex shrink-0 flex-wrap items-center gap-2">
          {countControl ?? <Badge status="info">{listenersCount(places)}</Badge>}
          {actions}
        </div>
      </div>
      {note ? <p className="text-12 text-red">{note}</p> : null}
      {named < places ? <p className="text-12 text-amber">{namedOfPlaces(named, places)}</p> : null}
      {shown.length > 0 ? (
        <ul className="flex flex-col gap-0.5 text-13" aria-label={`Слушатели — ${label}`}>
          {shown.map((e) => (
            <li key={e.personId} className="break-words">
              <span className="text-txt">{e.fullName}</span>
              {e.position ? <span className="text-txt-secondary"> — {e.position}</span> : null}
            </li>
          ))}
        </ul>
      ) : null}
      {rest > 0 ? (
        <div>
          <Button
            type="button"
            size="sm"
            variant="secondary"
            aria-expanded={expanded}
            aria-label={`${listenersToggleLabel(expanded, rest)} — ${label}`}
            onClick={() => setExpanded((v) => !v)}
          >
            {listenersToggleLabel(expanded, rest)}
          </Button>
        </div>
      ) : null}
    </li>
  )
}

/** Заголовок группы пакета: «Пакет «…»» и его программы строками программ. */
function PackageTitle({ pkg, showPrice }: { pkg: TrainingPackageComposition; showPrice: boolean }) {
  return (
    <div>
      <div className="flex items-baseline gap-3">
        <div className="min-w-0 flex-1 break-words font-semibold text-txt">Пакет «{pkg.name}»</div>
        {/* Команде — цена пакета (не программ внутри него: пакет стоит иначе). */}
        {showPrice && pkg.priceKopecks != null ? (
          <span className="shrink-0 text-13 text-txt-tertiary">
            {formatRubles(pkg.priceKopecks)}
          </span>
        ) : null}
      </div>
      <ul
        className="mt-2 flex flex-col gap-2 border-l-2 border-line pl-3"
        aria-label={`Программы пакета «${pkg.name}»`}
      >
        {pkg.programs.map((p) => (
          <li key={p.id}>
            <ProgramTitle program={p} />
          </li>
        ))}
      </ul>
    </div>
  )
}

export function SelectedPrograms({
  programs,
  packages = [],
  employees,
  renderActions,
  renderPackageActions,
  renderCount,
  renderPackageCount,
  title = 'Выбранные программы',
  draft = true,
  filling = false,
  showPrice = false,
  pinned = false,
  footer,
}: {
  programs: TrainingProgramComposition[]
  packages?: TrainingPackageComposition[]
  employees: TrainingSelectedEmployee[]
  renderActions?: (p: TrainingProgramComposition) => ReactNode
  renderPackageActions?: (p: TrainingPackageComposition) => ReactNode
  /** Поле числа слушателей программы (шаг выбора); без него — бейдж с числом. */
  renderCount?: (p: TrainingProgramComposition) => ReactNode
  renderPackageCount?: (p: TrainingPackageComposition) => ReactNode
  title?: string
  /** Черновик (иначе — возвращённый на доработку заказ): от этого зависят пометки недоступного. */
  draft?: boolean
  /** Заказ «На заполнении»: убрать недоступное — через «Изменить состав». */
  filling?: boolean
  /** Цена программы справа — только команде (редактор изменений состава). */
  showPrice?: boolean
  /**
   * Закреплённый блок (шаг выбора программ; ответ заказчицы 04.11 к п.4.1–4.3): остаётся под шапкой
   * кабинета, пока листается каталог, — зелёная жирная рамка, не выше доли экрана, длинный список
   * прокручивается внутри.
   */
  pinned?: boolean
  /** Под списком, закреплено вместе с ним (кнопки редактора изменений). */
  footer?: ReactNode
}) {
  const selected = programs.filter((p) => p.listenersCount > 0)
  const selectedPackages = packages.filter((p) => p.listenersCount > 0)
  const nothing = selected.length === 0 && selectedPackages.length === 0
  if (nothing && !footer) return null
  const byId = new Map(employees.map((e) => [e.personId, e]))
  const list = nothing ? null : (
    <ul
      className={cn(
        'divide-y divide-line bg-panel',
        pinned ? 'min-h-0 flex-1 overflow-y-auto' : 'rounded-10 border border-line',
      )}
    >
      {selectedPackages.map((pk) => (
        <SelectedListeners
          key={`package-${pk.id}`}
          title={<PackageTitle pkg={pk} showPrice={showPrice} />}
          label={`пакет «${pk.name}»`}
          places={pk.listenersCount}
          personIds={pk.selectedPersonIds}
          employees={byId}
          countControl={renderPackageCount?.(pk)}
          actions={renderPackageActions?.(pk)}
          note={
            pk.available
              ? undefined
              : filling
                ? UNAVAILABLE_PACKAGE_NOTE_FILLING
                : draft
                  ? UNAVAILABLE_PACKAGE_NOTE
                  : UNAVAILABLE_PACKAGE_NOTE_RETURNED
          }
        />
      ))}
      {selected.map((p) => (
        <SelectedListeners
          key={p.id}
          title={<ProgramTitle program={p} showPrice={showPrice} />}
          label={p.shortName || p.name}
          places={p.listenersCount}
          personIds={p.selectedPersonIds}
          employees={byId}
          countControl={renderCount?.(p)}
          actions={renderActions?.(p)}
          note={
            !p.archived
              ? undefined
              : filling
                ? ARCHIVED_PROGRAM_NOTE_FILLING
                : draft
                  ? ARCHIVED_PROGRAM_NOTE
                  : ARCHIVED_PROGRAM_NOTE_RETURNED
          }
        />
      ))}
    </ul>
  )
  if (!pinned) {
    return (
      <section aria-label={title}>
        <h3 className="mb-2 text-15 font-semibold text-txt">{title}</h3>
        {list}
        {footer ? <div className="mt-3">{footer}</div> : null}
      </section>
    )
  }
  return (
    <section
      aria-label={title}
      className="sticky top-topbar z-10 flex max-h-pinned-selection flex-col overflow-hidden rounded-12 border-2 border-green bg-panel"
    >
      <h3 className="shrink-0 border-b border-line px-3 py-2 text-15 font-semibold text-txt nw:px-4">
        {title}
      </h3>
      {list ?? (
        <p className="px-3 py-2 text-13 text-txt-secondary nw:px-4">Программы не выбраны.</p>
      )}
      {footer ? (
        <div className="shrink-0 border-t border-line px-3 py-2 nw:px-4">{footer}</div>
      ) : null}
    </section>
  )
}
